import { default as React, useRef } from 'react';
import TextField from '@mui/material/TextField';
import FormHelperText from '@mui/material/FormHelperText';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

/**
 * Custom Storage Size input field that is disabled for new deployments
 * and enabled for redeploy operations so users can update the storage size.
 * On redeploy, storage can only be increased (PVC limitation).
 */
export const StorageSizeInput = ({
  onChange,
  rawErrors,
  required,
  formData,
  formContext,
  schema,
}: FieldExtensionComponentProps<number>) => {
  const value = formData ?? schema.default ?? 10;

  // Enable editing only during redeploy operations
  const isRedeploy = formContext?.formData?.actionType === 'redeploy';
  const isDisabled = !isRedeploy;

  // Capture the initial (prefilled) storage size on first render for redeploy
  const initialSizeRef = useRef<number | null>(null);
  if (
    isRedeploy &&
    initialSizeRef.current === null &&
    formData !== null &&
    formData !== undefined
  ) {
    initialSizeRef.current = formData;
  }

  const minSize =
    isRedeploy && initialSizeRef.current !== null
      ? initialSizeRef.current
      : schema.minimum ?? 1;

  const numericValue = typeof value === 'number' ? value : Number(value);
  const isBelowMinimum =
    isRedeploy &&
    initialSizeRef.current !== null &&
    numericValue < initialSizeRef.current;

  const getHelperText = () => {
    if (isDisabled) {
      return 'Storage size can be changed during redeployment';
    }
    if (isBelowMinimum) {
      return `Storage size cannot be decreased below ${initialSizeRef.current}GB. Kubernetes PVCs only support increasing storage.`;
    }
    if (rawErrors?.length) {
      return rawErrors[0];
    }
    const description =
      schema.description || 'Size of persistent storage in GB';
    // Strip markdown bold syntax (**text**) to prevent raw asterisks from showing
    return description.replace(/\*\*([^*]+)\*\*/g, '$1');
  };

  return (
    <>
      <TextField
        label={schema.title || 'Storage Size (GB)'}
        type="number"
        value={value}
        onChange={e => {
          const newValue = Number(e.target.value);
          onChange(newValue);
        }}
        required={required}
        error={isBelowMinimum || (rawErrors?.length ?? 0) > 0}
        helperText={getHelperText()}
        disabled={isDisabled}
        fullWidth
        variant="outlined"
        margin="normal"
        inputProps={{
          min: minSize,
          max: schema.maximum ?? 100,
        }}
        InputProps={{
          readOnly: isDisabled,
        }}
      />
      {isRedeploy && initialSizeRef.current !== null && !isBelowMinimum && (
        <FormHelperText>
          Current size: {initialSizeRef.current}GB. Storage can only be
          increased due to Kubernetes PVC limitations.
        </FormHelperText>
      )}
    </>
  );
};
